/**
 * Rich-text reply composer for inbox Gmail/email drafts.
 * Sticky bottom panel with collapse/expand — keeps the conversation thread visible.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Bold, Italic, Underline, Link2, List, ListOrdered,
  Quote, Loader2, Send, Save, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  composerPayloadFromHtml,
  countDraftCharacters,
  countDraftCharactersFromHtml,
  initialComposerHtml,
  logDraftCharCount,
  runComposerCommand,
  toggleComposerList,
} from "@/lib/emailComposer";
import { toast } from "@/lib/toast";
import { CollapsibleComposerShell } from "@/components/inbox/CollapsibleComposerShell";

function ToolbarButton({ onClick, active, title, children, disabled }) {
  let ariaPressed;
  if (active !== undefined) {
    ariaPressed = active ? "true" : "false";
  }
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      // Preserve contentEditable selection — default mousedown steals focus
      // and list commands then apply to nothing.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onClick}
      aria-pressed={ariaPressed}
      className={cn(
        "inline-flex h-8 w-8 sm:h-6 sm:w-6 items-center justify-center rounded text-slate-600 shrink-0",
        "hover:bg-slate-100 disabled:opacity-40",
        active && "bg-indigo-50 text-indigo-700",
      )}
    >
      {children}
    </button>
  );
}

ToolbarButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};

export default function ReplyComposer({
  open,
  onOpenChange,
  mode = "edit",
  draftKey = "",
  forceExpandKey = "",
  conversationId = "",
  initialBodyHtml = "",
  initialBodyText = "",
  signature = "",
  quotedHtml = "",
  assistantName = "Maya",
  busy = false,
  onSave,
  onSend,
}) {
  const editorRef = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    bullet: false,
    numbered: false,
  });

  const seedHtml = useCallback(() => {
    const base = initialComposerHtml({
      bodyHtml: initialBodyHtml,
      bodyText: initialBodyText,
      signature: mode === "reply" && !initialBodyHtml && !initialBodyText ? signature : "",
    });
    if (mode === "reply" && quotedHtml && !initialBodyHtml && !initialBodyText) {
      return `${quotedHtml}${base}`;
    }
    return base || "<p><br></p>";
  }, [initialBodyHtml, initialBodyText, signature, quotedHtml, mode]);

  const hydrateEditor = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = seedHtml();
    el.innerHTML = html;
    // Count via stripHtml (same as view/save) — never contentEditable.innerText,
    // which injects browser-specific block newlines and shifts the counter on Edit.
    const fromEditor = countDraftCharactersFromHtml(html);
    const fromSource = countDraftCharacters({
      bodyText: initialBodyText,
      bodyHtml: initialBodyHtml,
    });
    setCharCount(fromEditor);
    logDraftCharCount("editor.hydrate", {
      sourceLength: fromSource,
      editorLength: fromEditor,
      unchanged: fromSource === fromEditor,
    });
  }, [seedHtml, initialBodyText, initialBodyHtml]);

  const syncToolbarState = useCallback(() => {
    if (typeof document === "undefined" || !editorRef.current) return;
    try {
      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        bullet: document.queryCommandState("insertUnorderedList"),
        numbered: document.queryCommandState("insertOrderedList"),
      });
    } catch {
      /* queryCommandState unsupported */
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    hydrateEditor();
    const frame = requestAnimationFrame(hydrateEditor);
    return () => cancelAnimationFrame(frame);
  }, [open, draftKey, hydrateEditor]);

  if (!open) return null;

  const afterEdit = () => {
    const html = editorRef.current?.innerHTML || "";
    const next = countDraftCharactersFromHtml(html);
    setCharCount(next);
    logDraftCharCount("editor.input", { length: next });
    syncToolbarState();
  };

  const exec = (command, value = null) => {
    runComposerCommand(editorRef.current, command, value);
    afterEdit();
  };

  const applyList = (ordered) => {
    const el = editorRef.current;
    if (!el) return;
    const ok = toggleComposerList(el, { ordered });
    if (process.env.NODE_ENV === "development" && !ok) {
      // eslint-disable-next-line no-console
      console.debug("composer.list_command", { ordered, applied: ok });
    }
    afterEdit();
  };

  const insertLink = () => {
    const url = window.prompt("Link URL");
    if (url) exec("createLink", url);
  };

  const readPayload = () => composerPayloadFromHtml(editorRef.current?.innerHTML || "");

  const handleSave = async () => {
    if (busy) return;
    const payload = readPayload();
    if (!payload.body_text.trim()) {
      toast.error("Draft cannot be empty");
      return;
    }
    await onSave?.(payload);
  };

  const handleSend = async () => {
    if (busy) return;
    const payload = readPayload();
    if (!payload.body_text.trim()) {
      toast.error("Message cannot be empty");
      return;
    }
    await onSend?.(payload);
  };

  const title = mode === "reply" ? "Reply" : "Edit draft";
  const storageKey = conversationId
    ? `reply:${conversationId}`
    : `reply:${draftKey || "default"}`;

  return (
    <CollapsibleComposerShell
      title={title}
      statusLabel="Draft"
      charCount={charCount}
      storageKey={storageKey}
      forceExpandKey={forceExpandKey || `${mode}:${draftKey}`}
      defaultExpanded
      expandOnMount
      defaultHeight={160}
      minHeight={120}
      testId="reply-composer-dialog"
      headerClassName="pr-1"
    >
      <div className="flex flex-col min-h-0 flex-1 h-full">
        <div className="flex items-center justify-end px-3 pb-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-slate-500"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            aria-label="Close composer"
          >
            <X size={13} />
          </Button>
        </div>

        <div className="mx-3 mb-0.5 rounded-md border border-slate-200 overflow-hidden flex flex-col min-h-0 flex-1">
          <fieldset
            className="flex items-center gap-0.5 px-1 py-1 border-b border-slate-100 bg-slate-50 shrink-0 overflow-x-auto scrollbar-thin"
          >
            <legend className="sr-only">Draft formatting</legend>
            <ToolbarButton title="Bold" disabled={busy} active={activeFormats.bold} onClick={() => exec("bold")}>
              <Bold size={13} />
            </ToolbarButton>
            <ToolbarButton title="Italic" disabled={busy} active={activeFormats.italic} onClick={() => exec("italic")}>
              <Italic size={13} />
            </ToolbarButton>
            <ToolbarButton title="Underline" disabled={busy} active={activeFormats.underline} onClick={() => exec("underline")}>
              <Underline size={13} />
            </ToolbarButton>
            <ToolbarButton title="Link" disabled={busy} onClick={insertLink}>
              <Link2 size={13} />
            </ToolbarButton>
            <ToolbarButton
              title="Bullet list"
              disabled={busy}
              active={activeFormats.bullet}
              onClick={() => applyList(false)}
            >
              <List size={13} />
            </ToolbarButton>
            <ToolbarButton
              title="Numbered list"
              disabled={busy}
              active={activeFormats.numbered}
              onClick={() => applyList(true)}
            >
              <ListOrdered size={13} />
            </ToolbarButton>
            <ToolbarButton title="Quote" disabled={busy} onClick={() => exec("formatBlock", "blockquote")}>
              <Quote size={13} />
            </ToolbarButton>
          </fieldset>
          <div
            key={draftKey || "composer-editor"}
            ref={editorRef}
            contentEditable={!busy}
            role="textbox"
            aria-multiline="true"
            aria-label="Reply body"
            tabIndex={busy ? -1 : 0}
            onInput={afterEdit}
            onKeyUp={syncToolbarState}
            onMouseUp={syncToolbarState}
            onFocus={syncToolbarState}
            className={cn(
              "min-h-[65px] flex-1 overflow-y-auto overflow-x-hidden",
              "px-2 py-1 text-[12.5px] leading-snug text-slate-800 focus:outline-none",
              "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1",
              "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1",
              "[&_li]:my-0.5",
              "[&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3",
              "[&_blockquote]:text-slate-600 [&_a]:text-indigo-600 [&_a]:underline",
              busy && "opacity-60 pointer-events-none",
            )}
            data-testid="reply-composer-editor"
            suppressContentEditableWarning
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-1.5 px-3 py-1.5 border-t border-slate-100 bg-white shrink-0">
          <Button variant="ghost" size="sm" className="h-9 sm:h-7 text-[11px] shrink-0" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9 sm:h-7 text-[11px] shrink-0"
            onClick={handleSave}
            disabled={busy}
            data-testid="reply-composer-save"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save draft
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={busy}
            className="h-9 sm:h-7 text-[11px] shrink-0 bg-indigo-600 hover:bg-indigo-700"
            data-testid="reply-composer-send"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Send
          </Button>
        </div>
      </div>
    </CollapsibleComposerShell>
  );
}

ReplyComposer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["edit", "reply"]),
  draftKey: PropTypes.string,
  forceExpandKey: PropTypes.string,
  conversationId: PropTypes.string,
  initialBodyHtml: PropTypes.string,
  initialBodyText: PropTypes.string,
  signature: PropTypes.string,
  quotedHtml: PropTypes.string,
  assistantName: PropTypes.string,
  busy: PropTypes.bool,
  onSave: PropTypes.func,
  onSend: PropTypes.func,
};
